import "./instrument";
import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/index.js';
import reportWebVitals from "./reportWebVitals";
import { AccountProvider } from "./contexts/accountContext";
import { SessionProvider } from "./components/sessionContext/sessionContext";
import CartProvider from "./contexts/cartContext";
import PopupProvider from "./contexts/popupContext";
import { EventsProvider } from "./contexts/eventsContext";
import { ProjectProvider } from "./contexts/projectContext";
import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/sora/600.css';
import '@fontsource/sora/700.css';
import './responsive-headings.css';
import './i18n/index.js';
import { HelmetProvider } from 'react-helmet-async';
import { CssBaseline } from '@mui/material';
import BackendConfig from "./backendConfig";
import NexThemeProvider from './contexts/themeProvider';

const useLocalApiProxy = process.env.REACT_APP_USE_LOCAL_API_PROXY === 'true';

function normalizeHost(value) {
  if (!value) return '';
  const raw = String(value).trim();
  // allow passing full URL (https://host)
  try {
    if (/^https?:\/\//i.test(raw)) return new URL(raw).hostname;
  } catch (_) {
    // fall through
  }
  return raw.replace(/^https?:\/\//i, '').replace(/\/.*/, '');
}

function ensureMetaTag(name, content) {
  if (typeof document === 'undefined') return;
  if (!content) return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function shouldLogHostConfig() {
  try {
    if (typeof window === 'undefined') return false;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('debugHost') === '1') return true;
    if (window.localStorage?.getItem('debugHost') === '1') return true;
  } catch (_) {
    // ignore
  }
  return false;
}

// Make NEURON host configurable by env for dev/prod builds.
// Order: sessionStorage override (if already set) -> env -> existing meta -> window.host.
(() => {
  if (typeof window === 'undefined') return;

  // IMPORTANT: The EventsLocal.js script and other early boot code may try to connect to
  // /ClientEventsWS based on the <meta name="NEURON"> tag.
  // If we fall back to window.location.hostname on Azure Static Web Apps, that will be the
  // SWA domain (not a Neuron host) and can trigger reload loops.
  const DEFAULT_NEURON_HOST = 'innova.tagroot.io';

  const envHost = normalizeHost(BackendConfig.neuronDomain);
  const existingMeta = document.querySelector('meta[name="NEURON"], meta[name="neuron"]')?.getAttribute('content');
  const metaHost = normalizeHost(existingMeta);

  const stored = normalizeHost(window.sessionStorage.getItem('AgentAPI.Host'));
  // Prefer build-time env host so deployments are deterministic per environment.
  // Session storage remains a manual override if env is not set.
  const finalHost = envHost || stored || metaHost || DEFAULT_NEURON_HOST;

  if (shouldLogHostConfig()) {
    console.info('[host-config] resolve', {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_NEURON_HOST: BackendConfig.neuronDomain,
      REACT_APP_NEX_BACKEND: BackendConfig.httpBackend,
      stored,
      metaHost,
      envHost,
      finalHost,
      locationHost: window.location.hostname,
    });
  }

  if (finalHost) {
    try {
      // AgentAPI normally builds absolute URLs from this value. In proxy mode
      // point it back at this CRA server so /Agent is forwarded server-side.
      const agentApiHost = useLocalApiProxy ? window.location.host : finalHost;
      if (!stored || stored !== agentApiHost) window.sessionStorage.setItem('AgentAPI.Host', agentApiHost);
      if (useLocalApiProxy) {
        window.sessionStorage.setItem('AgentAPI.Protocol', window.location.protocol.replace(':', ''));
      }
    } catch (_) { void 0; }
  }

  ensureMetaTag('NEURON', finalHost);
  ensureMetaTag('neuron', finalHost);
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <HelmetProvider>
    <CssBaseline />
    <I18nextProvider i18n={i18n}>
      <PopupProvider>
        <EventsProvider>
          <AccountProvider>
            <ProjectProvider>
              <CartProvider>
                <SessionProvider>
                  <React.StrictMode>
                    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>} showDialog={false}>
                      <App />
                    </Sentry.ErrorBoundary>
                  </React.StrictMode>
                </SessionProvider>
              </CartProvider>
            </ProjectProvider>
          </AccountProvider>
        </EventsProvider>
      </PopupProvider>
    </I18nextProvider>
  </HelmetProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
